import type {
  ExperimentGroup,
  ExperimentServiceError,
  ExperimentServiceMethods,
  NotificationItem,
  NotificationServiceMethods,
  Result,
} from '@forest/native-services'

/** One day in milliseconds; trial lengths and lead times are whole days. */
export const DAY_MS = 24 * 60 * 60 * 1000

/** Today's fixed reminder lead time: the trial-end reminder fires 2 days out. */
export const DEFAULT_REMINDER_LEAD_DAYS = 2

/** The reminder lead times Variant1 lets the user pick from. */
export const REMINDER_LEAD_DAY_OPTIONS = [2, 3] as const

export type ReminderLeadDays = (typeof REMINDER_LEAD_DAY_OPTIONS)[number]

/** Native's identifier for the trial reminder day selection experiment. */
export const TRIAL_REMINDER_EXPERIMENT = 'TrialReminderDaySelection'

/**
 * The arms of the trial reminder day selection experiment, spelled as native
 * spells them. Everything that is not a definite `Variant1` is Control.
 */
export const TRIAL_REMINDER_GROUPS = ['Control', 'Variant1'] as const

export type TrialReminderGroup = (typeof TRIAL_REMINDER_GROUPS)[number]

/**
 * The trial-end reminder's contract identifier, shared with native: both the
 * notification group and its single item key. Native keys its own scheduling
 * on it, so scheduling this group from web replaces native's reminder.
 */
export const TRIAL_END_REMINDER = 'trial_end_remind'

/** Resolves native's answer for the experiment into an arm. */
export function trialReminderGroupFrom(
  result: Result<ExperimentGroup, ExperimentServiceError>,
): TrialReminderGroup {
  if (result.success && result.data.group === 'Variant1') return 'Variant1'
  return 'Control'
}

/**
 * Asks native which arm the user is in. Never rejects: a thrown bridge error
 * (or the bridge's own call timeout) is as good as no answer, which is Control.
 */
export async function fetchTrialReminderGroup(
  getGroup: ExperimentServiceMethods['getGroup'],
): Promise<TrialReminderGroup> {
  try {
    return trialReminderGroupFrom(
      await getGroup({ experiment: TRIAL_REMINDER_EXPERIMENT }),
    )
  } catch {
    return 'Control'
  }
}

/**
 * The calendar day a reminder would arrive for a trial started today: local
 * today + trial length − reminder lead time, at local midnight (the option
 * rows show a day, not an instant).
 */
export function projectedReminderDate(input: {
  now: Date
  trialDays: number
  leadDays: number
}): Date {
  const { now, trialDays, leadDays } = input
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + trialDays - leadDays,
  )
}

/** The projected reminder day as the option rows show it, e.g. `Aug 6`. */
export function formatProjectedReminderDate(
  date: Date,
  locale: string,
): string {
  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    month: 'short',
    day: 'numeric',
  }).format(date)
}

/** What the user chose in the "Enable Trial End Reminder" dialog. */
export type ReminderDialogChoice = 'confirm' | 'dismiss'

/**
 * The collaborators the post-purchase trial reminder flow talks to. They are
 * injected so the flow can be driven by fakes at the module boundary
 * (native permission and notification bridges, the reminder dialog, error
 * telemetry).
 */
export interface TrialReminderDeps {
  hasNotificationPermission: () => Promise<Result<boolean>>
  requestNotificationPermission: () => Promise<Result<boolean>>
  /** Opens the reminder dialog; resolves with what the user chose. */
  openReminderDialog: (reminderDate: string) => Promise<ReminderDialogChoice>
  /** Schedules the trial-end reminder on native; only Variant1 reaches it. */
  scheduleNotification: NotificationServiceMethods['schedule']
  /** Where thrown bridge errors go; the user never sees them. */
  captureException: (error: unknown) => void
}

export interface TrialReminderInput {
  /** From the purchase facts: the resolved subscription is in its trial. */
  isTrialSuccess: boolean
  /** The subscription's expiry (ISO string), when known. */
  expiresAt?: string
  /** The experiment arm the trial was started under. */
  group: TrialReminderGroup
  /**
   * The reminder lead time picked on Variant1's selection page; `null` when
   * none was picked (Control never shows the page, and a reload loses it).
   */
  reminderLeadDays: ReminderLeadDays | null
  /** Paywall language param, e.g. `en`, `zh_tw`. */
  locale: string
}

/**
 * The instant the trial-end reminder fires: trial end minus the reminder lead
 * time, exact to the millisecond. This is the one place native's rule for the
 * lead time lives; the dialog date and `fire_at` both derive from it.
 */
export function reminderDateFor(
  expiresAt: string,
  reminderLeadDays: number,
): Date {
  return new Date(new Date(expiresAt).getTime() - reminderLeadDays * DAY_MS)
}

/** When the trial-end reminder fires as native wants it: whole epoch seconds. */
export function trialEndReminderFireAt(
  expiresAt: string,
  reminderLeadDays: number,
): number {
  return Math.floor(
    reminderDateFor(expiresAt, reminderLeadDays).getTime() / 1000,
  )
}

/** What the trial-end reminder is timed and worded from. */
export interface TrialEndReminderTiming {
  /** The subscription's expiry (ISO string). */
  expiresAt: string
  reminderLeadDays: ReminderLeadDays
  /** Paywall language param, e.g. `en`, `zh_tw`. */
  locale: string
}

/**
 * The single item of the web-scheduled trial-end reminder. Text goes as native
 * l10n keys with string arguments only — no baked-string fallback, since the
 * keys ship in the same native version that gates the service. `tap_url` and
 * `tracking_type` copy native's own push so it behaves identically apart from
 * the day.
 */
export function buildTrialEndReminderItem(
  input: TrialEndReminderTiming,
): NotificationItem {
  const { expiresAt, reminderLeadDays, locale } = input
  return {
    key: TRIAL_END_REMINDER,
    title_key: 'group_b_trial_end_reminder_title',
    title_args: [String(reminderLeadDays)],
    body_key: 'dialog_trial_end_reminder_content',
    body_args: [formatNumericDate(new Date(expiresAt), locale)],
    fire_at: trialEndReminderFireAt(expiresAt, reminderLeadDays),
    tap_url: 'forestapp://main_page/subscription_page',
    tracking_type: TRIAL_END_REMINDER,
  }
}

/** The paywall `language` param (`zh_tw`) as a BCP 47 tag Intl accepts. */
function toIntlLocale(locale: string): string {
  return locale.replace('_', '-')
}

/** A day as the dialog and the push body show it: numeric y/m/d in the paywall language. */
function formatNumericDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(date)
}

/**
 * The reminder lead time web schedules for: the user's choice in Variant1,
 * `null` whenever native's own reminder stands (Control, or Variant1 with no
 * choice left after a reload).
 */
function reminderLeadDaysScheduledByWeb(
  input: Pick<TrialReminderInput, 'group' | 'reminderLeadDays'>,
): ReminderLeadDays | null {
  return input.group === 'Variant1' ? input.reminderLeadDays : null
}

/**
 * Post-purchase trial reminder flow. After a trial start, when notification
 * permission is missing, show the reminder dialog and, if the user opts in,
 * request permission. In Variant1 with a chosen lead time, web then schedules
 * the trial-end reminder itself (unless the user said "No Need"), replacing
 * native's 2-day default. Never throws — a failing bridge call is reported to
 * telemetry and the purchase success path continues untouched.
 */
export async function runTrialReminderFlow(
  input: TrialReminderInput,
  deps: TrialReminderDeps,
): Promise<void> {
  if (!input.isTrialSuccess || !input.expiresAt) return
  const { expiresAt, locale } = input
  const webReminderLeadDays = reminderLeadDaysScheduledByWeb(input)
  const reminderLeadDays = webReminderLeadDays ?? DEFAULT_REMINDER_LEAD_DAYS

  const outcome = await offerReminderPermission(
    { expiresAt, reminderLeadDays, locale },
    deps,
  )
  if (outcome === 'declined' || webReminderLeadDays === null) return

  // Fire-and-forget: the success view never waits for native's answer, and
  // the answer only ever reaches telemetry or the console.
  void scheduleTrialEndReminder(
    buildTrialEndReminderItem({
      expiresAt,
      reminderLeadDays: webReminderLeadDays,
      locale,
    }),
    deps,
  )
}

/**
 * Shows the reminder dialog when permission is definitely missing and, on
 * "Remind Me", requests it. `declined` is the user's "No Need"; `settled`
 * covers every other path — granted, unknown, requested (whatever native
 * answered) — because native remains the only truth on permission.
 */
async function offerReminderPermission(
  input: TrialEndReminderTiming,
  deps: TrialReminderDeps,
): Promise<'declined' | 'settled'> {
  let permission: Result<boolean>
  try {
    permission = await deps.hasNotificationPermission()
  } catch (error) {
    deps.captureException(error)
    return 'settled'
  }
  // Only a definite "not granted" opens the dialog; an unknown state is left
  // alone, matching the behaviour the flow replaced.
  if (!permission.success || permission.data) return 'settled'

  const reminderDate = formatNumericDate(
    reminderDateFor(input.expiresAt, input.reminderLeadDays),
    input.locale,
  )
  const choice = await deps.openReminderDialog(reminderDate)
  if (choice !== 'confirm') return 'declined'

  try {
    await deps.requestNotificationPermission()
  } catch (error) {
    deps.captureException(error)
  }
  return 'settled'
}

/**
 * Replaces the `trial_end_remind` group with the one item. Only a malformed
 * request or a thrown bridge call is worth telemetry; `permission_denied`,
 * `quota_exceeded`, `unsupported`, and an empty answer are expected outcomes
 * that leave native's own reminder (or none) in place.
 */
async function scheduleTrialEndReminder(
  item: NotificationItem,
  deps: TrialReminderDeps,
): Promise<void> {
  try {
    const result = await deps.scheduleNotification({
      group: TRIAL_END_REMINDER,
      items: [item],
    })
    if (!result.success) {
      if (result.error === 'invalid_request') {
        deps.captureException(
          new Error(`${TRIAL_END_REMINDER} schedule rejected: invalid_request`),
        )
      } else {
        console.warn(
          `[trial-reminder] ${TRIAL_END_REMINDER} not scheduled:`,
          result.error,
        )
      }
      return
    }
    if (result.data.scheduled.length === 0) {
      console.warn(
        `[trial-reminder] native scheduled nothing for ${TRIAL_END_REMINDER}`,
      )
    }
  } catch (error) {
    deps.captureException(error)
  }
}
